import { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useHasEnablement } from '@portal/pkg-authorization'
import type { RoleDetailResponse } from '@portal/pkg-api/gen/admin-console'
import {
  buildPermissionGroups,
  filterGroups,
  toEntitlementUpdates,
  type PermissionGroup,
} from '../model/permissionGroups'
import { useUpdateRoleEntitlements } from '../mutations'
import { EntitlementQueries } from '../queries'

function PermissionSection({
  group,
  checked,
  readOnly,
  onToggle,
  onSelectAll,
}: {
  group: PermissionGroup
  checked: ReadonlySet<string>
  readOnly: boolean
  onToggle: (authority: string) => void
  onSelectAll: (group: PermissionGroup) => void
}) {
  const { t } = useTranslation('admin-console')
  const allChecked = group.items.every((item) => checked.has(item.authority))

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1,
          backgroundColor: 'grey.50',
        }}
      >
        <Typography variant="subtitle2">{group.label}</Typography>
        {!readOnly && (
          <Link component="button" type="button" variant="body2" onClick={() => onSelectAll(group)}>
            {t(allChecked ? 'roles.access.clearAll' : 'roles.access.selectAll')}
          </Link>
        )}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
          px: 2,
          py: 1,
        }}
      >
        {group.items.map((item) => (
          <FormControlLabel
            key={item.authority}
            control={
              <Checkbox
                size="small"
                checked={checked.has(item.authority)}
                disabled={readOnly}
                onChange={() => onToggle(item.authority)}
              />
            }
            label={<Typography variant="body2">{item.label}</Typography>}
          />
        ))}
      </Box>
    </Paper>
  )
}

export function AccessTab({ role }: { role: RoleDetailResponse }) {
  const { t } = useTranslation('admin-console')
  const { enqueueSnackbar } = useSnackbar()
  const [search, setSearch] = useState('')

  const { data: available } = useSuspenseQuery(EntitlementQueries.available(role.id, role.name))
  const { data: granted } = useSuspenseQuery(EntitlementQueries.granted(role.id, role.name))
  const updateEntitlements = useUpdateRoleEntitlements(role.name)

  /**
   * `permissionsEditable` is the server's verdict, which already accounts for the Role.ADMIN
   * authority the built-in roles need and for organization 1's Default being editable by nobody.
   * The client adds only the entitlement the endpoint itself requires.
   */
  const canManage = useHasEnablement('Role', 'MANAGE', { global: true })
  const readOnly = !canManage || !role.permissionsEditable

  const labelFor = (authority: string) =>
    t(`roles.permissions.entitlement.${authority.replaceAll('.', '_')}`, {
      defaultValue: authority,
    })
  const featureLabelFor = (feature: string) =>
    t(`roles.permissions.feature.${feature}`, { defaultValue: feature })

  const groups = useMemo(
    () => buildPermissionGroups(available, granted, labelFor, featureLabelFor),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [available, granted, t],
  )

  /**
   * Seeded from what the role holds and edited locally until saved, so the boxes stay where the
   * user put them while the save is in flight and a failed save does not silently revert them.
   */
  const [checked, setChecked] = useState<ReadonlySet<string>>(
    () => new Set(granted.map((entitlement) => entitlement.authority)),
  )

  const grantedAuthorities = useMemo(
    () => new Set(granted.map((entitlement) => entitlement.authority)),
    [granted],
  )
  const isDirty =
    checked.size !== grantedAuthorities.size ||
    [...checked].some((authority) => !grantedAuthorities.has(authority))

  const visible = filterGroups(groups, search)

  const toggle = (authority: string) =>
    setChecked((previous) => {
      const next = new Set(previous)
      if (!next.delete(authority)) next.add(authority)
      return next
    })

  /** Acts on the section as the user currently sees it, so a search narrows what it reaches. */
  const selectAll = (group: PermissionGroup) =>
    setChecked((previous) => {
      const next = new Set(previous)
      const allChecked = group.items.every((item) => next.has(item.authority))
      for (const item of group.items) {
        if (allChecked) next.delete(item.authority)
        else next.add(item.authority)
      }
      return next
    })

  const save = () =>
    updateEntitlements.mutate(
      { id: role.id, entitlements: toEntitlementUpdates(available, checked) },
      {
        onSuccess: () => enqueueSnackbar(t('roles.access.saved'), { variant: 'success' }),
        onError: () => enqueueSnackbar(t('roles.access.saveFailed'), { variant: 'error' }),
      },
    )

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          size="small"
          variant="outlined"
          placeholder={t('roles.access.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ '& .MuiOutlinedInput-root': { height: 32 }, width: 280 }}
        />
        {!readOnly && (
          <Button
            variant="contained"
            size="small"
            disabled={!isDirty || updateEntitlements.isPending}
            onClick={save}
          >
            {t('roles.access.saveChanges')}
          </Button>
        )}
      </Box>

      {readOnly && <Typography variant="body2">{t('roles.access.readOnly')}</Typography>}

      {visible.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 4, textAlign: 'center' }}>
          {t('roles.access.noResults')}
        </Typography>
      ) : (
        visible.map((group) => (
          <PermissionSection
            key={group.feature}
            group={group}
            checked={checked}
            readOnly={readOnly}
            onToggle={toggle}
            onSelectAll={selectAll}
          />
        ))
      )}
    </Stack>
  )
}
